import { useState, useRef } from "react";
import Lottie from "lottie-react";
import "../../styles/ApplyToday.css";
import characterAnimation from "../../assets/animations/character.json";
import { API_URL } from "../../config/apiConfig";

function ApplyToday() {
  // File size limit: 1 MB
  const MAX_FILE_SIZE = 1048576; // 1 MB in bytes
  const ALLOWED_FILE_TYPES = ["application/pdf", "image/jpeg", "image/png"];

  const [form, setForm] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    suffix: "",
    ssn: "",
    dob: "",
    address1: "",
    address2: "",
    country: "United States",
    city: "",
    state: "",
    zip: "",
    residence3yrs: "",
    primaryPhone: "",
    cellPhone: "",
    email: "",
    confirmEmail: "",
    consent: false,
    license: "",
    licenseFile: null,
    immigrationFile: null,
    otherDocument: null,
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [hint, setHint] = useState("👋 Hi! I’ll help you fill this form.");
  const lottieRef = useRef();

  const play = (from, to) => {
    if (lottieRef.current) {
      lottieRef.current.playSegments([from, to], true);
    }
  };

  // Validate file size and type
  const validateFile = (file, fileName) => {
    if (!file) return { valid: false, error: `${fileName} is required.` };

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return {
        valid: false,
        error: `❌ ${fileName} must be a PDF or JPEG file. Got: ${file.type || "Unknown format"}`,
      };
    }

    if (file.size > MAX_FILE_SIZE) {
      const sizeMB = (file.size / 1048576).toFixed(2);
      return {
        valid: false,
        error: `❌ ${fileName} is too large (${sizeMB} MB). Maximum allowed size is 1 MB.`,
      };
    }

    return { valid: true, error: null };
  };

  const getGuidanceHint = (fieldName, value) => {
    const hints = {
      // Personal Information
      firstName: "📝 Great! Now add your middle name.",
      middleName: "✅ Perfect! Let's get your last name.",
      lastName: "👏 Excellent! Do you have a suffix?",
      ssn: "🎯 Got your SIN/HST! Now your date of birth.",
      dob: "🗓️ Thanks! Now your license number.",
      license: "🚗 License number saved! Now upload your license photo.",
      licenseFile: "📸 License photo received! Now upload immigration document.",
      immigrationFile: "🛂 Immigration document saved! Now upload other documents.",
      otherDocument: "📄 Great! Now let's fill your address.",
      
      // Address
      address1: "📍 Address saved! Add line 2 if needed.",
      address2: "📍 Perfect! Now your city.",
      city: "🏙️ Got your city! Now the state/province.",
      state: "🏞️ State saved! Now your zip code.",
      zip: "📮 Great! Now let's add your contact info.",
      
      // Contact Information
      primaryPhone: "📱 Phone saved! Now your cell phone.",
      cellPhone: "📞 Perfect! Now your email address.",
      email: "📧 Email saved! Confirm it by entering again.",
      confirmEmail: "✅ All set! Now let's finalize.",
      
      // Consent
      consent: "🎉 Almost there! Review and submit your application!",
    };

    return hints[fieldName] || "✍️ Keep filling the form!";
  };

  const getAnimationSegment = (fieldName) => {
    const segments = {
      // Personal Information animations
      firstName: [0, 10],
      middleName: [10, 20],
      lastName: [20, 30],
      ssn: [30, 40],
      dob: [40, 50],
      license: [50, 60],
      licenseFile: [60, 70],
      immigrationFile: [70, 80],
      otherDocument: [80, 90],
      
      // Address animations
      address1: [90, 100],
      address2: [100, 110],
      city: [110, 120],
      state: [120, 130],
      zip: [130, 140],
      
      // Contact animations
      primaryPhone: [140, 150],
      cellPhone: [150, 160],
      email: [160, 170],
      confirmEmail: [170, 180],
      
      // Final animations
      consent: [180, 200],
    };

    return segments[fieldName] || [0, 10];
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === "file") {
      const file = files[0];
      if (file) {
        // Map field names to readable names
        const fieldNameMap = {
          licenseFile: "License Photo",
          immigrationFile: "Immigration Document",
          otherDocument: "Other Documents",
        };
        const readableName = fieldNameMap[name] || name;

        // Validate the file
        const validation = validateFile(file, readableName);
        if (!validation.valid) {
          setMessage(validation.error);
          // Reset the file input
          e.target.value = "";
          return;
        }

        // File is valid, clear any previous error message and update form
        setMessage("");
        setForm((prev) => ({ ...prev, [name]: file }));
        
        // Add guidance for file uploads
        const guideHint = getGuidanceHint(name, file.name);
        setHint(guideHint);
        const [from, to] = getAnimationSegment(name);
        play(from, to);
      }
      return;
    }

    const val = type === "checkbox" ? checked : value;
    setForm((prev) => ({ ...prev, [name]: val }));
    
    // Show guidance hint when field has value
    if (value || checked) {
      const guideHint = getGuidanceHint(name, val);
      setHint(guideHint);
      
      // Trigger animation
      const [from, to] = getAnimationSegment(name);
      play(from, to);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!API_URL) {
      setMessage("❌ API URL not configured. Contact developer.");
      setHint("❌ System configuration error. Please contact support.");
      return;
    }

    // Validate all required documents
    if (!form.licenseFile) {
      setMessage("❌ License Photo is required.");
      setHint("📸 Please upload your license photo to continue.");
      play(60, 70);
      return;
    }

    if (!form.immigrationFile) {
      setMessage("❌ Immigration Document is required.");
      setHint("🛂 Please upload your immigration document to continue.");
      play(70, 80);
      return;
    }

    if (!form.otherDocument) {
      setMessage("❌ Other Documents are required.");
      setHint("📄 Please upload other documents to continue.");
      play(80, 90);
      return;
    }

    // Validate file sizes and types before submission
    const licenseValidation = validateFile(form.licenseFile, "License Photo");
    if (!licenseValidation.valid) {
      setMessage(licenseValidation.error);
      return;
    }

    const immigrationValidation = validateFile(
      form.immigrationFile,
      "Immigration Document"
    );
    if (!immigrationValidation.valid) {
      setMessage(immigrationValidation.error);
      return;
    }

    const otherValidation = validateFile(form.otherDocument, "Other Documents");
    if (!otherValidation.valid) {
      setMessage(otherValidation.error);
      return;
    }

    if (form.email !== form.confirmEmail) {
      setMessage("❌ Emails do not match.");
      setHint("📧 Please make sure your emails match!");
      return;
    }

    if (!form.consent) {
      setMessage("❌ Consent is required.");
      setHint("✅ Please check the consent checkbox to continue.");
      play(180, 200);
      return;
    }

    try {
      setLoading(true);
      setHint("⏳ Submitting your application...");
      setMessage("");

      // Check if API URL is configured
      if (!API_URL || API_URL.trim() === "") {
        throw new Error("API URL is not configured. Please contact support.");
      }

      const formData = new FormData();
      Object.keys(form).forEach((key) => {
        formData.append(key, form[key]);
      });

      // Log files being sent
      console.log("📤 Files to upload:");
      console.log("  - License File:", form.licenseFile ? `${form.licenseFile.name} (${(form.licenseFile.size / 1024).toFixed(2)} KB)` : "Missing");
      console.log("  - Immigration File:", form.immigrationFile ? `${form.immigrationFile.name} (${(form.immigrationFile.size / 1024).toFixed(2)} KB)` : "Missing");
      console.log("  - Other Document:", form.otherDocument ? `${form.otherDocument.name} (${(form.otherDocument.size / 1024).toFixed(2)} KB)` : "Missing");

      const submitUrl = `${API_URL}/apply`;
      console.log("Submitting to:", submitUrl);

      const response = await fetch(submitUrl, {
        method: "POST",
        body: formData,
      });

      console.log("Response status:", response.status);

      // Try to parse response as JSON
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error("Failed to parse response as JSON:", parseError);
        data = {};
      }

      if (!response.ok) {
        const errorMessage = data.message || `Server error: ${response.status}`;
        throw new Error(errorMessage);
      }

      setMessage("🎉 Application submitted successfully!");
      setHint("🎉 Congratulations! Your application has been submitted. Our team will review it shortly!");
      play(180, 200);
      
      setForm({
        firstName: "",
        middleName: "",
        lastName: "",
        suffix: "",
        ssn: "",
        dob: "",
        address1: "",
        address2: "",
        country: "United States",
        city: "",
        state: "",
        zip: "",
        residence3yrs: "",
        primaryPhone: "",
        cellPhone: "",
        email: "",
        confirmEmail: "",
        consent: false,
        license: "",
        licenseFile: null,
        immigrationFile: null,
        otherDocument: null,
      });
    } catch (error) {
      console.error("Form submission error:", error);
      
      // Provide more specific error messages
      let displayMessage = "❌ ";
      
      if (error.message.includes("Failed to fetch")) {
        displayMessage += "Unable to connect to server. Check your internet connection and try again.";
        setHint("🌐 Connection issue. Please check your internet and try again.");
      } else if (error.message.includes("API URL is not configured")) {
        displayMessage += error.message;
        setHint("⚙️ System configuration error. Please contact support.");
      } else if (error.message.includes("License Photo") || error.message.includes("Immigration Document") || error.message.includes("Other Documents")) {
        displayMessage += error.message;
        setHint("📎 Please check your document uploads.");
      } else {
        displayMessage += error.message || "Submission failed. Please try again.";
        setHint("😟 Oops! Something went wrong. Please try again.");
      }
      
      setMessage(displayMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="apply-container">
      <h1>Apply Today – Join the RGM Family</h1>
      <p>Please complete the application below.</p>

      <div className="character-wrapper">
        <Lottie
          animationData={characterAnimation}
          loop
          lottieRef={lottieRef}
          className="character-animation"
        />
        <div className="speech-bubble">{hint}</div>
      </div>

      <form className="apply-form" onSubmit={handleSubmit}>
        <div className="section-card full">
          <h3>Personal Information</h3>

          <input name="firstName" placeholder="First Name *" required value={form.firstName} onChange={handleChange} />
          <input name="middleName" placeholder="Middle Name" value={form.middleName} onChange={handleChange} />
          <input name="lastName" placeholder="Last Name *" required value={form.lastName} onChange={handleChange} />
          <input name="suffix" placeholder="Suffix" value={form.suffix} onChange={handleChange} />
          <input name="ssn" placeholder="SIN/HST *" required value={form.ssn} onChange={handleChange} />
          <input type="date" name="dob" required value={form.dob} onChange={handleChange} />
          <input name="license" placeholder="License Number *" required value={form.license} onChange={handleChange} />

          <label>
            Upload License Photo *
            <input type="file" name="licenseFile" accept=".jpg,.jpeg,.png,.pdf" required onChange={handleChange} />
            <small style={{ color: "#666", marginTop: "5px", display: "block" }}>Max file size: 1 MB. Allowed formats: PDF, JPEG, PNG</small>
          </label>

          <label>
            Upload Immigration Document *
            <input type="file" name="immigrationFile" accept=".jpg,.jpeg,.png,.pdf" required onChange={handleChange} />
            <small style={{ color: "#666", marginTop: "5px", display: "block" }}>Max file size: 1 MB. Allowed formats: PDF, JPEG, PNG</small>
          </label>
        </div>

        <div className="section-card full">
          <h3>Address</h3>
          <input name="address1" placeholder="Address Line 1 *" required value={form.address1} onChange={handleChange} />
          <input name="address2" placeholder="Address Line 2" value={form.address2} onChange={handleChange} />
          <input name="city" placeholder="City *" required value={form.city} onChange={handleChange} />
          <input name="state" placeholder="State *" required value={form.state} onChange={handleChange} />
          <input name="zip" placeholder="PINCODE *" required value={form.zip} onChange={handleChange} />
        </div>

        <div className="section-card full">
          <h3>Contact</h3>

          <input name="primaryPhone" placeholder="Primary Phone *" required value={form.primaryPhone} onChange={handleChange} />
          <input name="email" type="email" placeholder="Email *" required value={form.email} onChange={handleChange} />
          <input name="confirmEmail" type="email" placeholder="Confirm Email *" required value={form.confirmEmail} onChange={handleChange} />

          <label>
            Upload Other Documents *
            <input type="file" name="otherDocument" accept=".jpg,.jpeg,.png,.pdf" required onChange={handleChange} />
            <small style={{ color: "#666", marginTop: "5px", display: "block" }}>Max file size: 1 MB. Allowed formats: PDF, JPEG, PNG</small>
          </label>

          <label className="checkbox-container">
            <input type="checkbox" name="consent" checked={form.consent} onChange={handleChange} />
            I consent to receive communication and confirmation
          </label>
        </div>

        {message && <div className="form-message">{message}</div>}

        <button className="submit-btn full" type="submit" disabled={loading}>
          {loading ? "Submitting..." : "Submit Application"}
        </button>
      </form>
    </div>
  );
}

export default ApplyToday;
